import { defineStore } from 'pinia'
import { ApiRes, ApiWalletResponse, BankAccount, getLegalWallet, getNaturalWallet, MoneyWithdrawal } from '../api'
import { TransactionMethod, TransactionType, WalletStatus } from '../../typings'
import { isLegalProfile } from '../investment-process/useUserProfileType'
import { useLegalAccountDataState } from './useAccountDataLegal'
import { getPlatformSettings } from '../usePlatformSettings'

export interface Wallet {
    id: string
    type: 'lemonway' | 'secupay'
    status: WalletStatus
    balance: number
    retrievable_balance: number
    currency: string
    currency_symbol: string
    deposit_account_holder: string
    deposit_payment_reference: string
    deposit_iban: string
    deposit_bic: string
    limit_credit_card: number
    limit_bank_transfer: number
    logo_url: string
    can_deposit: boolean
    can_withdraw: boolean
}

export interface WalletTransaction {
    id: string
    type: TransactionType
    date: string | Date
    from: string
    to: string
    amount: number
    signed_amount: number
    status: string
    internal_status: string
    internal_status_text: string
    method: TransactionMethod
    order_id: string
    asset_title: string
    transaction_purpose_text: string
}

export interface WalletState {
    wallet?: Wallet | null
    wallets?: Wallet[] | null
    transactions: WalletTransaction[]
    bankAccounts: BankAccount[]
    moneyWithdrawals: MoneyWithdrawal[]
    loaded: boolean
    loading: boolean
    saving: boolean
    filteredTransactions: WalletTransaction[]

    fetchWalletData?(): Promise<void>
    fetchWalletDataSilent?(): Promise<void>
    setFilteredTransactions(type: 'secupay' | 'lemonway'): void
    verifiedBankAccounts?: BankAccount[]
}

export const useWalletState = defineStore('wallet-data', {
    state: (): WalletState => {
        return {
            wallet: null,
            wallets: null,
            transactions: [],
            bankAccounts: [],
            moneyWithdrawals: [],
            loading: false,
            loaded: false,
            saving: false,
            filteredTransactions: [],
        }
    },
    actions: {
        setWallet(wallet: Wallet) {
            this.wallet = wallet
        },
        setFilteredTransactions(type: 'secupay' | 'lemonway') {
            this.filteredTransactions = this.transactions.filter((transaction) => transaction.wallet_type === type)
        },
        async fetchWalletData() {
            this.loading = true
            try {
                const response = await getWallet()
                this.loading = false
                this.loaded = true
                this.wallets = response.data.wallets
                this.transactions = response.data.transactions
                this.wallets.forEach((wallet, index) => {
                    wallet.bankAccounts.forEach((bankAccount) => {
                        bankAccount.wallet_type = wallet.type
                    })
                    wallet.moneyWithdrawals.forEach((moneyWithdrawal) => {
                        moneyWithdrawal.wallet_type = wallet.type
                    })
                    this.bankAccounts.push(...this.wallets[index].bankAccounts)
                    this.moneyWithdrawals.push(...this.wallets[index].moneyWithdrawals)
                    this.wallet = this.wallets.find(
                        (wallet) => wallet.type === getPlatformSettings().active_payment_account
                    )
                })
            } catch (error) {
                console.log(error)
            } finally {
                this.loading = false
            }
        },
        async fetchWalletDataSilent() {
            try {
                const response = await getWallet()
                this.$patch(response.data)
            } catch (error) {
                console.log(error)
            }
        },
    },
    getters: {
        // filteredTransactions(): WalletTransaction[] {
        //     return this.transactions.filter(
        //         (transaction) =>
        //             transaction.status === TransactionStatus.SUCCESS_MONEY_IN_OUT_OR_PENDING_P2P ||
        //             transaction.status === TransactionStatus.SUCCESS_P2P_OR_MONEY_IN_OUT_ERROR
        //     )
        // },
        verifiedBankAccounts(): BankAccount[] {
            return this.bankAccounts.filter(
                (account) => account.status === 'verified' && account.wallet_type === this.wallet?.type
            )
        },
    },
})

const getWallet = (): ApiRes<ApiWalletResponse> => {
    if (isLegalProfile()) {
        const accountStateLegal = useLegalAccountDataState()
        return accountStateLegal.fetchAccountData().then(() => {
            const legalProfile = accountStateLegal.legalProfile
            return getLegalWallet(legalProfile.legal_profile_uid)
        })
    } else {
        return getNaturalWallet()
    }
}
